// TODO(entity-rollout): TitleRow takes an MUI icon component; move it to
// EntityIcon (action) once TitleRow takes Lucide glyphs.
import { Box, Divider, useTheme } from "@mui/material";
import * as React from "react";

import { useAuth, useNavigation } from "@/providers";
import { UpdateDatasetTags } from "@/shared/components/datasets";
import { useSidebarControls } from "@/shared/components/MainLayoutWithSidebar";
import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { toDate, useDateTimeFormat } from "@/shared/datetime";
import {
  useActiveInvocationCount,
  useTerminalInvocationCount,
} from "@/shared/domain/actions/hooks";
import { DatasetRecord } from "@/shared/domain/datasets";
import {
  useDataset,
  useDatasetFileCount,
} from "@/shared/domain/datasets/hooks";
import {
  AutoAwesomeOutlinedIcon,
  DataObjectIcon,
  FolderOpenIcon,
  InfoOutlinedIcon,
  KeyboardTabIcon,
  LabelOutlinedIcon,
  ShareIcon,
} from "@/shared/foundation/icons/mui";
import { Principal } from "@/shared/principal";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { Dataset } from "@/types";

import { KeyValuePair } from "./KeyValuePair";
import { TitleRow } from "./TitleRow";

export interface DatasetDetailSidebarContentProps {
  dataset?: Dataset;
  onLinkCopied?: () => void;
  onDatasetUpdated?: (record: DatasetRecord) => void;
}

export const DatasetDetailSidebarContent: React.FC<
  DatasetDetailSidebarContentProps
> = ({ dataset, onLinkCopied, onDatasetUpdated }) => {
  const theme = useTheme();
  const { goto } = useNavigation();
  const sidebarControls = useSidebarControls();

  const handleClose = () => {
    if (sidebarControls.isOpen) {
      sidebarControls.toggleOpen();
    }
  };

  const onClickLink = async () => {
    const link = `${window.location.href}/${dataset?.dataset_id}`;

    try {
      await navigator.clipboard.writeText(link);

      if (onLinkCopied) {
        onLinkCopied();
      }
    } catch (error) {
      ErrorMonitoringService.captureError(error);
    }
  };

  const dateTimeFormat = useDateTimeFormat();
  const created = toDate(dataset?.created);
  const createdDateString =
    created === null ? "" : dateTimeFormat.format(created, { time: "none" });
  const modified = toDate(dataset?.modified);
  const modifiedDateString =
    modified === null ? "" : dateTimeFormat.format(modified);

  const { currentOrganization } = useAuth();

  const fileCountQuery = useDatasetFileCount(
    dataset?.dataset_id ?? "",
    currentOrganization?.org_id,
  );

  // Lazy-load the full dataset record to get metadata, which is not included
  // in search results. This may become a dedicated metadata endpoint in the future.
  const datasetQuery = useDataset(dataset?.dataset_id);

  const activeInvocationCountQuery = useActiveInvocationCount(
    dataset?.dataset_id,
    { refetchInterval: false },
  );

  const terminalInvocationCountQuery = useTerminalInvocationCount(
    dataset?.dataset_id,
  );

  React.useEffect(() => {
    if (datasetQuery.error) {
      ErrorMonitoringService.captureError(datasetQuery.error);
    }
  }, [datasetQuery.error]);

  React.useEffect(() => {
    if (activeInvocationCountQuery.error) {
      ErrorMonitoringService.captureError(activeInvocationCountQuery.error);
    }
  }, [activeInvocationCountQuery.error]);

  React.useEffect(() => {
    if (terminalInvocationCountQuery.error) {
      ErrorMonitoringService.captureError(terminalInvocationCountQuery.error);
    }
  }, [terminalInvocationCountQuery.error]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: theme.spacing(2),
        }}
      >
        <Box
          sx={{
            display: "flex",
          }}
        >
          <FolderOpenIcon
            sx={{
              marginRight: theme.spacing(1.25),
              color: theme.palette.text.primary,
            }}
          />
          <RobotoTypography
            variant="body1"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              marginRight: theme.spacing(1),
            }}
          >
            {dataset?.dataset_id}
          </RobotoTypography>
        </Box>
        <RobotoIconButton
          eventName={"DatasetSidebarClosed"}
          sx={{ p: 0.5 }}
          onClick={handleClose}
        >
          <KeyboardTabIcon />
        </RobotoIconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          marginBottom: theme.spacing(2),
          gap: theme.spacing(1.5),
        }}
      >
        <RobotoButton
          variant={"contained"}
          onClick={() => {
            goto.dataset({ datasetId: dataset?.dataset_id ?? "" });
          }}
          eventName={"DatasetOpened"}
          eventProperties={{
            datasetId: dataset?.dataset_id ?? "",
          }}
        >
          Open
        </RobotoButton>

        <RobotoButton
          variant={"outlined"}
          startIcon={<ShareIcon />}
          onClick={() => {
            void onClickLink();
          }}
          eventName={"ShareDatasetClicked"}
          eventProperties={{
            datasetId: dataset?.dataset_id ?? "",
          }}
        >
          Share
        </RobotoButton>
      </Box>

      <Divider sx={{ marginBottom: theme.spacing(2) }} />

      <Box
        sx={{
          marginBottom: theme.spacing(2),
        }}
      >
        <TitleRow title={"Details"} IconComponent={InfoOutlinedIcon} />
        <Box>
          <KeyValuePair label="Name" value={dataset?.name || "N/A"} />
          <KeyValuePair label="Created" value={createdDateString} />
          <KeyValuePair label="Modified" value={modifiedDateString} />
          <KeyValuePair
            label="Created By"
            value={
              dataset?.created_by
                ? Principal.fromString(dataset?.created_by).ok?.id
                : ""
            }
          />
          <KeyValuePair
            label="Files"
            value={`${fileCountQuery.data ?? ""}`}
            isLoading={fileCountQuery.data === undefined}
          />
        </Box>
      </Box>

      <Divider sx={{ marginBottom: theme.spacing(2) }} />

      <Box
        sx={{
          marginBottom: theme.spacing(2),
        }}
      >
        <TitleRow title={"Tags"} IconComponent={LabelOutlinedIcon} />

        <Box>
          {dataset?.dataset_id && (
            <UpdateDatasetTags
              initialTags={dataset?.tags || []}
              datasetId={dataset?.dataset_id}
              currentOrg={currentOrganization}
              onTagsUpdated={onDatasetUpdated}
            />
          )}
        </Box>
      </Box>

      {datasetQuery.isLoading && (
        <>
          <Divider sx={{ marginBottom: theme.spacing(2) }} />
          <Box sx={{ marginBottom: theme.spacing(2) }}>
            <TitleRow title={"Metadata"} IconComponent={DataObjectIcon} />
            <Box>
              <KeyValuePair label="" value="" isLoading={true} />
            </Box>
          </Box>
        </>
      )}

      {datasetQuery.isSuccess &&
        datasetQuery.data?.metadata &&
        Object.keys(datasetQuery.data.metadata).length > 0 && (
          <>
            <Divider sx={{ marginBottom: theme.spacing(2) }} />
            <Box sx={{ marginBottom: theme.spacing(2) }}>
              <TitleRow title={"Metadata"} IconComponent={DataObjectIcon} />
              <Box>
                {Object.keys(datasetQuery.data.metadata).map((key) => {
                  return (
                    <KeyValuePair
                      key={key}
                      label={key}
                      value={datasetQuery.data?.metadata?.[key] ?? ""}
                    />
                  );
                })}
              </Box>
            </Box>
          </>
        )}

      {datasetQuery.isError && (
        <>
          <Divider sx={{ marginBottom: theme.spacing(2) }} />
          <Box sx={{ marginBottom: theme.spacing(2) }}>
            <TitleRow title={"Metadata"} IconComponent={DataObjectIcon} />
            <RobotoTypography
              variant="body2"
              sx={{ color: theme.palette.error.main }}
            >
              Failed to load metadata
            </RobotoTypography>
          </Box>
        </>
      )}

      <Divider sx={{ marginBottom: theme.spacing(2) }} />

      <Box sx={{ marginBottom: theme.spacing(2) }}>
        <TitleRow title={"Actions"} IconComponent={AutoAwesomeOutlinedIcon} />
        <KeyValuePair
          label="Running"
          value={`${activeInvocationCountQuery.data ?? ""}`}
          isLoading={activeInvocationCountQuery.isPending}
        />
        <KeyValuePair
          label="Completed"
          value={`${terminalInvocationCountQuery.data ?? ""}`}
          isLoading={terminalInvocationCountQuery.isPending}
        />
      </Box>
    </Box>
  );
};
